import { useEffect, useState } from 'react'
import { getDashboardWidget } from '../../../api/insights'
import { label } from '../../../lib/labels'
import type { CreditValueByJurisdictionResult, StatusCountBucket } from '../../../types/insights'

export interface DashboardFilterOption {
  value: string
  label: string
}

export interface DashboardFilterOptionsResult {
  fiscalYears: DashboardFilterOption[]
  jurisdictions: DashboardFilterOption[]
  statuses: DashboardFilterOption[]
  loading: boolean
}

/**
 * Populates the fiscal-year / jurisdiction / filing-status dashboard filter pickers from data the
 * dashboard already fetches for its own tiles (IB-311 Slice 1) — no new backend endpoint. Each list
 * is derived from its own **unfiltered** read (never the current, possibly-narrowed dashboard
 * filter) so picking one filter never shrinks what the OTHER pickers can offer — same "criteria
 * independence" the URL-state filter machinery already relies on:
 *   - fiscal year + jurisdiction options <- `credit-value-by-jurisdiction-fy` (already zero-filled
 *     server-side per known jurisdiction, AC25 — the same read `CreditValueByJurisdictionFyWidget`
 *     renders).
 *   - filing-status options <- `filings-by-status` (business-labelled, zero-filled per known
 *     `ProgramFilingState`).
 * A `not_authorized`/`error`/`coming_soon` response for either just leaves that picker's option
 * list empty — same honest-degrade posture as every other dashboard tile, never a thrown error.
 */
export function useDashboardFilterOptions(): DashboardFilterOptionsResult {
  const [fiscalYears, setFiscalYears] = useState<DashboardFilterOption[]>([])
  const [jurisdictions, setJurisdictions] = useState<DashboardFilterOption[]>([])
  const [statuses, setStatuses] = useState<DashboardFilterOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    let alive = true

    Promise.allSettled([
      getDashboardWidget<CreditValueByJurisdictionResult>('credit-value-by-jurisdiction-fy', {}, controller.signal),
      getDashboardWidget<StatusCountBucket[]>('filings-by-status', {}, controller.signal),
    ]).then(([creditResult, statusResult]) => {
      if (!alive) return

      if (creditResult.status === 'fulfilled' && creditResult.value.status === 'ok' && creditResult.value.data) {
        const buckets = Array.isArray(creditResult.value.data.buckets) ? creditResult.value.data.buckets : []
        const fyIds = new Set<string>()
        const jur = new Set<string>()
        for (const b of buckets) {
          if (b.fiscalYearId) fyIds.add(b.fiscalYearId)
          if (b.jurisdiction) jur.add(b.jurisdiction)
        }
        setFiscalYears([...fyIds].sort().map((id) => ({ value: id, label: id })))
        setJurisdictions([...jur].sort().map((code) => ({ value: code, label: label('jurisdiction', code) })))
      }

      if (statusResult.status === 'fulfilled' && statusResult.value.status === 'ok' && Array.isArray(statusResult.value.data)) {
        setStatuses(statusResult.value.data.map((b) => ({ value: b.status, label: b.label })))
      }

      setLoading(false)
    })

    return () => {
      alive = false
      controller.abort()
    }
  }, [])

  return { fiscalYears, jurisdictions, statuses, loading }
}
